import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCircleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../../contexts/AuthContext";
import {
  completeOnboarding,
  createOnboardingProgress,
  getOnboardingProgress,
  syncUserToBrevo,
} from "@/shared/db";
import { analytics } from "@/utils/analytics";
import { ROUTES } from "@/shared/core";

/**
 * Client onboarding: branded welcome experience that marks onboarding complete
 * and redirects to dashboard. Clients don't need the multi-step lawyer flow.
 */
export const ClientOnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const hasTrackedStarted = useRef(false);

  useEffect(() => {
    const complete = async () => {
      if (!user) {
        navigate("/auth");
        return;
      }
      try {
        if (!hasTrackedStarted.current) {
          hasTrackedStarted.current = true;
          analytics.onboardingStarted("client");
        }
        const existing = await getOnboardingProgress(user.id);
        if (!existing) {
          await createOnboardingProgress(user.id, 1);
        }
        await completeOnboarding(user.id);
        analytics.onboardingCompleted("client");
        await syncUserToBrevo(user.id);
        await refreshProfile();
        // Brief pause so user sees the success state
        await new Promise((r) => setTimeout(r, 1600));
        navigate(ROUTES.DASHBOARD);
      } catch (err) {
        console.error("Client onboarding error:", err);
        navigate(ROUTES.DASHBOARD);
      }
    };
    complete();
  }, [user, navigate, refreshProfile]);

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(160deg, #f1fcfb 0%, #fcfaf8 50%, #f0fdf4 100%)",
        }}
      />
      <div
        className="absolute inset-0 -z-10 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0z' fill='%23164b4a' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`,
        }}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md px-6 text-center"
      >
        {/* Logo */}
        <Link
          to="/"
          className="inline-block mb-10 opacity-90 hover:opacity-100 transition-opacity duration-200"
          aria-label="Lawgentia Home"
        >
          <img
            src="/images/brand/lawgentia-logo.png"
            alt="Lawgentia"
            className="h-10 w-auto mx-auto"
          />
        </Link>

        {/* Success icon */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 24 }}
          className="mx-auto w-24 h-24 rounded-full bg-linear-to-br from-tiber-400 to-tiber-600 flex items-center justify-center shadow-lg shadow-tiber-500/30 mb-8"
        >
          <CheckCircleIcon className="w-14 h-14 text-white" strokeWidth={1.5} />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          className="text-2xl sm:text-3xl font-bold text-tiber-900 tracking-tight mb-3"
        >
          {t("onboarding.client.welcomeTitle", "You're all set!")}
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.4 }}
          className="text-tiber-600 text-base sm:text-lg leading-relaxed mb-10"
        >
          {t(
            "onboarding.client.welcomeMessage",
            "Your account is ready. You'll be redirected to your dashboard in a moment."
          )}
        </motion.p>

        {/* Loading indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-center gap-2 text-tiber-600/80 text-sm"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <SparklesIcon className="w-5 h-5" />
          </motion.div>
          <span>
            {t("onboarding.client.settingUp", "Setting up your account...")}
          </span>
        </motion.div>
      </motion.div>
    </div>
  );
};
